import React, { useMemo, useState } from 'react';
import { useStore } from '../state/store';
import { Card, Chip, Banner, Empty, Select, SectionHead, EvidenceBadge, Stat } from '../components/ui';
import EvidenceForm from '../components/EvidenceForm';
import { EVIDENCE_LEVELS, ACTIVITY, EMPTY_COPY } from '../lib/constants';
import { fmtDate } from '../lib/dates';
import { orgMarkets } from '../lib/orgPlan';

export default function Evidence() {
  const { state, d, remove } = useStore();
  const ev = d.evidence;
  const markets = orgMarkets(state).map((m) => m.label);
  const [open, setOpen] = useState(false);
  const [market, setMarket] = useState('All');
  const [level, setLevel] = useState('All');

  const rows = useMemo(
    () =>
      ev.all.filter(
        (e) => (market === 'All' || e.market === market) && (level === 'All' || e.level === level)
      ),
    [ev.all, market, level]
  );

  return (
    <>
      <div className="page-head">
        <div className="row">
          <h2>EVIDENCE LADDER</h2>
          <div className="spacer" />
          <button className="btn primary sm" onClick={() => setOpen(true)}>Record evidence</button>
        </div>
        <div className="sub">
          There is no aggregate percentage here on purpose. A percentage would let motion average out into progress.
        </div>
      </div>

      <section className="grid g4">
        <Card><Stat label="E4 commercial commitments" term="evidenceE4" value={<span className="num">{ev.e4}</span>} size="lg" /></Card>
        <Card><Stat label="E5 binding proof" term="evidenceE5" value={<span className="num">{ev.e5}</span>} size="lg" /></Card>
        <Card><Stat label="E4 + E5 this week" term="e4E5Commitments" value={<span className="num">{ev.e4Week + ev.e5Week}</span>} size="lg" /></Card>
        <Card><Stat label="Activities logged" term="evidenceAct" value={<span className="num">{ev.activities.length}</span>} size="lg" foot="Not traction" /></Card>
      </section>

      <section className="section">
        <SectionHead title="The ladder" hint="Climb it. Do not redefine it." term="evidenceLadder" />
        <div className="ladder">
          <div className="rung act">
            <span className="id">ACT</span>
            <span className="copy">
              <span className="nm">{ACTIVITY.name}</span>
              <span className="ln">{ACTIVITY.line} {ACTIVITY.note}</span>
            </span>
            <span className={`ct ${ev.activities.length === 0 ? 'zero' : ''}`}>{ev.activities.length}</span>
          </div>
          {EVIDENCE_LEVELS.map((l) => {
            const count = ev.byLevel[l.id] || 0;
            return (
              <div className={`rung ${l.id === 'E4' ? 'e4' : ''} ${l.id === 'E5' ? 'e5' : ''}`} key={l.id}>
                <span className="id">{l.id}</span>
                <span className="copy">
                  <span className="nm">{l.name}</span>
                  <span className="ln">{l.line}</span>
                  <span className="ln dim">{l.note}</span>
                </span>
                <span className={`ct ${count === 0 ? 'zero' : ''}`}>{count}</span>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section">
        <SectionHead title="Evidence by market" hint="E4 and E5 counts, not a blended score." />
        <div className="grid g4">
          {markets.map((m) => {
            const b = ev.byMarket[m] || { total: 0, E4: 0, E5: 0, items: [] };
            return (
              <Card key={m} title={m} right={<Chip tone={b.E5 > 0 ? 'green' : b.E4 > 0 ? 'amber' : ''}>{b.total} record(s)</Chip>}>
                <div className="grid g2">
                  <Stat label="E4" term="evidenceE4" value={<span className="num">{b.E4}</span>} />
                  <Stat label="E5" term="evidenceE5" value={<span className="num">{b.E5}</span>} />
                </div>
                <div className="hr" />
                {b.items.slice(0, 3).map((e) => (
                  <div className="row tight" key={e.id} style={{ marginBottom: 6, alignItems: 'flex-start' }}>
                    <EvidenceBadge level={e.level} />
                    <span className="tiny" style={{ flex: 1 }}>{e.title}</span>
                  </div>
                ))}
                {b.total === 0 && <span className="dim tiny">Nothing recorded.</span>}
              </Card>
            );
          })}
        </div>
      </section>

      <section className="section">
        <SectionHead
          title="All records"
          right={
            <div className="row tight">
              <select value={market} onChange={(e) => setMarket(e.target.value)} style={{ width: 170 }}>
                <option value="All">All markets</option>
                {markets.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <select value={level} onChange={(e) => setLevel(e.target.value)} style={{ width: 190 }}>
                <option value="All">All levels</option>
                <option value="ACT">Activity</option>
                {EVIDENCE_LEVELS.map((l) => <option key={l.id} value={l.id}>{l.id} — {l.name}</option>)}
              </select>
            </div>
          }
        />
        {rows.length === 0 ? (
          <Empty title={EMPTY_COPY.evidence}>
            Record the first thing a counterparty did — not the first thing you did.
          </Empty>
        ) : (
          <Card flush>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ width: 70 }}>Level</th>
                    <th style={{ width: 90 }}>Date</th>
                    <th style={{ width: 120 }}>Market</th>
                    <th>What happened</th>
                    <th style={{ width: 180 }}>Counterparty</th>
                    <th className="actions" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e) => (
                    <tr key={e.id}>
                      <td><EvidenceBadge level={e.level} /></td>
                      <td className="num">{fmtDate(e.date)}</td>
                      <td>{e.market}</td>
                      <td>
                        <div>{e.title}</div>
                        {e.description && <div className="dim tiny">{e.description}</div>}
                      </td>
                      <td>{e.counterparty || <span className="dim">unnamed</span>}</td>
                      <td className="actions">
                        <button className="btn sm ghost" onClick={() => remove('evidence', e.id)}>Delete</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className="section">
        <Banner>
          <strong>Activity versus proof.</strong> Researched 20 companies → Activity. Sent an email → Activity.           Meeting
          booked → E1. Sponsor confirms a real problem or use case → E2. Counterparty commits information, time, or
          resources (named asset, documents, data room) → E3. Customer
          requested a priced proposal → E4. Customer paid → E5. E1 is never called traction. A named asset is E3, not E2.
        </Banner>
      </section>

      {open && <EvidenceForm onClose={() => setOpen(false)} />}
    </>
  );
}
